import {
  BookMarked,
  BookOpen,
  Briefcase,
  Camera,
  Clapperboard,
  ClipboardList,
  Cloud,
  Gamepad2,
  Globe,
  GraduationCap,
  Grid3x3,
  HeartPulse,
  LayoutGrid,
  LayoutList,
  MapPin,
  Music2,
  Newspaper,
  Palette,
  Plane,
  Settings,
  ShoppingBag,
  Smile,
  Sparkles,
  Stethoscope,
  Tag,
  Terminal,
  Trophy,
  Users,
  UtensilsCrossed,
  Wallet,
  Wrench,
  type LucideIcon,
} from "lucide-react";

import {
  normalizeCategoryLabel,
  resolveDisplayCategory,
  type CanonicalCategoryKey,
} from "./categoryNormalization";
import type { TranslationStructure } from "../../i18n";

export {
  isFallbackAppCategory,
  normalizeCategoryBucket,
  normalizeCategoryLabel,
  resolveAppCategory,
  resolveCanonicalCategoryKey,
  resolveDisplayCategory,
  SCREEN0_BUNDLE,
  SCREEN0_CATEGORY,
  type CanonicalCategoryKey,
} from "./categoryNormalization";

const CATEGORY_ICONS: Record<CanonicalCategoryKey, LucideIcon> = {
  books: BookOpen,
  business: Briefcase,
  catalogs: LayoutList,
  developerTools: Terminal,
  education: GraduationCap,
  entertainment: Clapperboard,
  finance: Wallet,
  foodAndDrink: UtensilsCrossed,
  games: Gamepad2,
  graphicsAndDesign: Palette,
  healthAndFitness: HeartPulse,
  kids: Smile,
  lifestyle: Sparkles,
  medical: Stethoscope,
  miscellaneous: LayoutGrid,
  music: Music2,
  navigation: MapPin,
  news: Newspaper,
  newsstand: BookOpen,
  photoAndVideo: Camera,
  productivity: ClipboardList,
  reference: BookMarked,
  shopping: ShoppingBag,
  socialNetworking: Users,
  sports: Trophy,
  stickers: Tag,
  travel: Plane,
  system: Settings,
  utilities: Wrench,
  weather: Cloud,
  uncategorized: Grid3x3,
  other: Grid3x3,
  website: Globe,
};

export function resolveCategoryIcon(key: CanonicalCategoryKey): LucideIcon {
  return CATEGORY_ICONS[key];
}

export function localizedCategoryLabel(
  key: CanonicalCategoryKey,
  t: Pick<TranslationStructure, "categories">,
): string {
  return t.categories[key];
}

export function categoryDisplayLabel(
  label: string,
  t: Pick<TranslationStructure, "categories">,
): string | null {
  const key = normalizeCategoryLabel(label);
  return key ? localizedCategoryLabel(key, t) : null;
}

/** Localized category label for app rows; unknown raw labels pass through unchanged. */
export function appCategoryDisplayLabel(
  category: string | null | undefined,
  t: Pick<TranslationStructure, "categories">,
  appIdentifier?: string | null,
): string {
  const trimmed = resolveDisplayCategory(appIdentifier, category);
  if (!trimmed) return localizedCategoryLabel("other", t);
  const key = normalizeCategoryLabel(trimmed);
  return key ? localizedCategoryLabel(key, t) : trimmed;
}

// The full set of canonical category lookupKeys used to purge bad Brandfetch mappings.
// These are generated by appBrandfetchLookupKey() when called with category display names.
export const CATEGORY_BRANDFETCH_LOOKUP_KEYS: readonly string[] = [
  "name:books",
  "name:business",
  "name:catalogs",
  "name:developer tools",
  "name:education",
  "name:entertainment",
  "name:finance",
  "name:food and drink",
  "name:games",
  "name:graphics and design",
  "name:health and fitness",
  "name:kids",
  "name:lifestyle",
  "name:medical",
  "name:miscellaneous",
  "name:music",
  "name:navigation",
  "name:news",
  "name:newsstand",
  "name:photo and video",
  "name:productivity",
  "name:reference",
  "name:shopping",
  "name:social networking",
  "name:social",
  "name:social network",
  "name:sports",
  "name:stickers",
  "name:travel",
  "name:system",
  "name:utilities",
  "name:weather",
  "name:uncategorized",
  "name:other",
  "name:website",
  // Italian (normalizeText strips accents, producing truncated keys)
  "name:produttivit",
  "name:utilit",
  "name:intrattenimento",
  "name:finanza",
  "name:giochi",
  "name:notizie",
  "name:musica",
  "name:viaggi",
  "name:salute e benessere",
  "name:foto e video",
  "name:istruzione",
  "name:riferimenti",
  "name:medicina",
  "name:stile di vita",
  "name:bambini",
  "name:meteo",
  "name:navigazione",
  "name:libri",
  "name:acquisti",
  "name:sport",
];
